import React, { useState, useEffect } from 'react';
import { Offcanvas } from 'react-bootstrap';
import axios from 'axios';
import Swal from 'sweetalert2';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const stageProbabilities = {
    'approach': 20,
    'prospect': 40,
    'negotiation': 60,
    'order': 80,
    'closed won': 100,
    'closed lost': 0,
};

const QuotationUploadOffcanvas = ({
    show,
    handleClose,
    deal,
    quotationData,
    categories = [],
    onSuccess,
}) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const userInfo = JSON.parse(localStorage.getItem('user-info') || '{}');
    const origin = localStorage.getItem('user-origin') || '';
    const token = userInfo?.token;

    const [licenseTypes, setLicenseTypes] = useState(categories || []);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [uploadFormData, setUploadFormData] = useState({
        client_name: '',
        project_name: '',
        ref_no: '',
        subscription_amount: '',
        deployment_amount: '',
        total_amount: '',
        quotation_date: new Date().toISOString().split('T')[0],
        license_type_id: '',
        model_type: '',
        quotation_file: null,
        isEditing: false,
        editingQuotationId: null,
    });

    // Fetch license types if not provided
    useEffect(() => {
        if (!categories || categories.length === 0) {
            const fetchLicenseTypes = async () => {
                try {
                    const response = await axios.get(`${backendUrl}/api/categories?type=product`, {
                        headers: { Authorization: `Bearer ${token}`, 'Tenant-Origin': origin },
                        skipLoader: true,
                    });
                    if (response.data && response.data.category) {
                        const rootCats = response.data.category.filter(c => !c.parent_id);
                        setLicenseTypes(rootCats);
                    }
                } catch (err) {
                    console.error('Error fetching categories for quotation upload:', err);
                }
            };
            if (show) {
                fetchLicenseTypes();
            }
        } else {
            setLicenseTypes(categories);
        }
    }, [categories, show, backendUrl, token, origin]);

    // Populate form data whenever show or quotationData or deal changes
    useEffect(() => {
        if (!show) return;

        const clientName = deal?.organisation?.name || deal?.client?.name || quotationData?.client?.name || '';
        const projectName = deal?.name || deal?.project?.name || quotationData?.project?.name || '';

        if (quotationData) {
            setUploadFormData({
                client_name: clientName,
                project_name: projectName,
                ref_no: quotationData.ref_no || '',
                subscription_amount: quotationData.total_amount_with_subscription || quotationData.subscription_amount || '',
                deployment_amount: quotationData.deplyment_amount || '',
                total_amount: quotationData.amount || '',
                quotation_date: quotationData.date
                    ? (quotationData.date.includes('T') ? quotationData.date.split('T')[0] : quotationData.date)
                    : new Date().toISOString().split('T')[0],
                license_type_id: quotationData.license_type_id || '',
                model_type: quotationData.model_type || '',
                quotation_file: null,
                isEditing: true,
                editingQuotationId: quotationData.id,
            });
        } else {
            setUploadFormData({
                client_name: clientName,
                project_name: projectName,
                ref_no: '',
                subscription_amount: '',
                deployment_amount: '',
                total_amount: '',
                quotation_date: new Date().toISOString().split('T')[0],
                license_type_id: '',
                model_type: '',
                quotation_file: null,
                isEditing: false,
                editingQuotationId: null,
            });
        }
    }, [show, quotationData, deal]);

    const handleUploadInputChange = (e) => {
        const { name, value, files } = e.target;
        if (name === 'quotation_file') {
            setUploadFormData(prev => ({
                ...prev,
                [name]: files[0]
            }));
        } else {
            setUploadFormData(prev => {
                const newState = {
                    ...prev,
                    [name]: value
                };
                if (name === 'subscription_amount' || name === 'deployment_amount') {
                    const sub = parseFloat(name === 'subscription_amount' ? value : prev.subscription_amount) || 0;
                    const dep = parseFloat(name === 'deployment_amount' ? value : prev.deployment_amount) || 0;
                    newState.total_amount = (sub + dep).toString();
                }
                return newState;
            });
        }
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!uploadFormData.ref_no) {
            Swal.fire({ toast: true, position: 'top-end', icon: 'warning', title: 'Please enter quotation reference number', timer: 3000, showConfirmButton: false });
            return;
        }

        setIsSubmitting(true);
        try {
            if (uploadFormData.isEditing && uploadFormData.editingQuotationId) {
                // EDIT / VERSION PATH
                const formData = new FormData();
                formData.append('ref_no', uploadFormData.ref_no);
                formData.append('amount', uploadFormData.total_amount);
                formData.append('date', uploadFormData.quotation_date);
                formData.append('total_amount_with_subscription', uploadFormData.subscription_amount || 0);
                formData.append('deplyment_amount', uploadFormData.deployment_amount || 0);
                formData.append('license_type_id', uploadFormData.license_type_id || '');
                formData.append('model_type', uploadFormData.model_type || '');
                formData.append('issales', 'true');
                formData.append('is_uploaded', 'true');
                if (uploadFormData.quotation_file) {
                    formData.append('quotation_file', uploadFormData.quotation_file);
                }
                formData.append('_method', 'PUT');

                const response = await axios.post(
                    `${backendUrl}/api/quotations/${uploadFormData.editingQuotationId}?_method=PUT`,
                    formData,
                    { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data', 'Tenant-Origin': origin } }
                );

                const newQuotation = response.data.quotation;

                // Sync deal if deal exists
                if (deal?.id && newQuotation) {
                    const prob = stageProbabilities[deal.stage] || 0;
                    const tot = parseFloat(uploadFormData.total_amount) || parseFloat(newQuotation.amount) || 0;
                    try {
                        await axios.put(`${backendUrl}/api/deals/${deal.id}`, {
                            name: deal.name,
                            stage: deal.stage,
                            value: tot,
                            expected_revenue: tot * (prob / 100),
                            quotation_id: String(newQuotation.id),
                            one_time_cost: uploadFormData.deployment_amount || newQuotation.deplyment_amount || 0,
                            recurring_cost: uploadFormData.subscription_amount || newQuotation.total_amount_with_subscription || 0,
                        }, { headers: { Authorization: `Bearer ${token}`, 'Tenant-Origin': origin } });
                    } catch (dErr) {
                        console.error('Error updating deal:', dErr);
                    }
                }

                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: `New version created: ${newQuotation?.ref_no || uploadFormData.ref_no}`,
                    timer: 3500,
                    showConfirmButton: false,
                });

                if (onSuccess) onSuccess(newQuotation);
                handleClose();
                return;
            }

            // CREATE PATH
            if (!uploadFormData.quotation_file) {
                Swal.fire({ toast: true, position: 'top-end', icon: 'warning', title: 'Please select a quotation file to upload', timer: 3000, showConfirmButton: false });
                setIsSubmitting(false);
                return;
            }

            const formData = new FormData();
            if (deal?.client_id) formData.append('client_id', deal.client_id);
            if (deal?.project_id) formData.append('project_id', deal.project_id);
            formData.append('ref_no', uploadFormData.ref_no);
            formData.append('amount', uploadFormData.total_amount);
            formData.append('date', uploadFormData.quotation_date);
            formData.append('total_amount_with_subscription', uploadFormData.subscription_amount || 0);
            formData.append('deplyment_amount', uploadFormData.deployment_amount || 0);
            formData.append('license_type_id', uploadFormData.license_type_id || '');
            formData.append('model_type', uploadFormData.model_type || '');
            formData.append('quotation_status', 'open');
            formData.append('issales', 'true');
            formData.append('is_uploaded', 'true');
            formData.append('products[0]', '');
            formData.append('quotation_file', uploadFormData.quotation_file);

            const response = await axios.post(`${backendUrl}/api/quotations`, formData, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data', 'Tenant-Origin': origin },
            });

            const createdQuotation = response.data.quotation;

            if (deal?.id && createdQuotation) {
                const prob = stageProbabilities[deal.stage] || 0;
                const tot = parseFloat(uploadFormData.total_amount) || parseFloat(createdQuotation.amount) || 0;
                await axios.put(`${backendUrl}/api/deals/${deal.id}`, {
                    name: deal.name,
                    stage: deal.stage,
                    value: tot,
                    expected_revenue: tot * (prob / 100),
                    quotation_id: String(createdQuotation.id),
                    one_time_cost: uploadFormData.deployment_amount || createdQuotation.deplyment_amount || 0,
                    recurring_cost: uploadFormData.subscription_amount || createdQuotation.total_amount_with_subscription || 0,
                }, { headers: { Authorization: `Bearer ${token}`, 'Tenant-Origin': origin } });
            }

            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: 'Quotation uploaded successfully!',
                timer: 3000,
                showConfirmButton: false,
            });

            if (onSuccess) onSuccess(createdQuotation);
            handleClose();

        } catch (error) {
            console.error('Error submitting uploaded quotation:', error);
            let errorMessage = 'Failed to save quotation';
            if (error.response?.data?.errors) {
                errorMessage = Object.values(error.response.data.errors).flat().join(', ');
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }
            Swal.fire({ toast: true, position: 'top-end', icon: 'error', title: errorMessage, timer: 5000, showConfirmButton: false });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Offcanvas
            show={show}
            onHide={handleClose}
            placement="end"
            backdrop={true}
            scroll={false}
            style={{ width: '580px', zIndex: 1055 }}
        >
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>
                    {uploadFormData.isEditing ? 'Edit Uploaded Quotation' : 'Upload Quotation'}
                </Offcanvas.Title>
            </Offcanvas.Header>
            <hr style={{ margin: 0 }} />
            <Offcanvas.Body>
                {/* Client Name (Read-only) */}
                <div className="mb-3">
                    <label className="form-label form-font">Client Name</label>
                    <input
                        type="text"
                        className="form-control form-feild"
                        name="client_name"
                        value={uploadFormData.client_name}
                        readOnly
                        disabled
                    />
                </div>

                {/* Project Name (Read-only) */}
                <div className="mb-3">
                    <label className="form-label form-font">Project Name</label>
                    <input
                        type="text"
                        className="form-control form-feild"
                        name="project_name"
                        value={uploadFormData.project_name}
                        readOnly
                        disabled
                    />
                </div>

                {/* Quotation Date */}
                <div className="mb-3">
                    <label className="form-label form-font">Quotation Date</label>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            value={uploadFormData.quotation_date ? dayjs(uploadFormData.quotation_date) : null}
                            onChange={(newValue) => {
                                handleUploadInputChange({
                                    target: {
                                        name: 'quotation_date',
                                        value: newValue ? newValue.format('YYYY-MM-DD') : ''
                                    }
                                });
                            }}
                            format="DD/MM/YYYY"
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    fullWidth: true,
                                    className: 'form-feild',
                                    sx: {
                                        '& .MuiInputBase-root': {
                                            fontSize: '13px',
                                            backgroundColor: '#fff',
                                            borderRadius: '14px',
                                            border: '0.5px solid rgb(232, 233, 237)',
                                            padding: '0px',
                                            height: '39px',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            border: 'none',
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '13px',
                                            padding: '8px 12px',
                                        },
                                    }
                                },
                                popper: {
                                    style: { zIndex: 99999 },
                                    placement: 'bottom-start',
                                },
                                desktopPaper: {
                                    style: { zIndex: 99999 },
                                },
                            }}
                        />
                    </LocalizationProvider>
                </div>

                {/* Reference Number */}
                <div className="mb-3">
                    <label className="form-label form-font">Reference No <span className="text-danger">*</span></label>
                    <input
                        type="text"
                        className="form-control form-feild"
                        name="ref_no"
                        value={uploadFormData.ref_no}
                        onChange={handleUploadInputChange}
                        placeholder="Enter reference number"
                        required
                    />
                </div>

                {/* License Type Dropdown */}
                <div className="mb-3">
                    <label className="form-label form-font">License Type <span className="text-danger">*</span></label>
                    <select
                        className="form-select form-feild"
                        name="license_type_id"
                        value={uploadFormData.license_type_id}
                        onChange={handleUploadInputChange}
                        required
                    >
                        <option value="">Select License Type</option>
                        {licenseTypes?.map(type => (
                            <option key={type.id} value={type.id}>
                                {type.name}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Subscription Amount */}
                <div className="mb-3">
                    <label className="form-label form-font">Subscription Amount</label>
                    <input
                        type="number"
                        className="form-control form-feild"
                        name="subscription_amount"
                        value={uploadFormData.subscription_amount}
                        onChange={handleUploadInputChange}
                    />
                </div>

                {/* Deployment Amount */}
                <div className="mb-3">
                    <label className="form-label form-font">Deployment Amount</label>
                    <input
                        type="number"
                        className="form-control form-feild"
                        name="deployment_amount"
                        value={uploadFormData.deployment_amount}
                        onChange={handleUploadInputChange}
                    />
                </div>

                {/* Total Amount */}
                <div className="mb-3">
                    <label className="form-label form-font">Total Amount</label>
                    <input
                        type="number"
                        className="form-control form-feild"
                        name="total_amount"
                        value={uploadFormData.total_amount}
                        onChange={handleUploadInputChange}
                    />
                </div>

                {/* File Upload */}
                <div className="mb-3">
                    <label className="form-label form-font">
                        Quotation File {uploadFormData.isEditing ? <small className="text-muted">(Optional for revision)</small> : <span className="text-danger">*</span>}
                    </label>
                    <input
                        type="file"
                        className="form-control form-feild"
                        name="quotation_file"
                        onChange={handleUploadInputChange}
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                        required={!uploadFormData.isEditing}
                    />
                    <small className="text-muted">Supported formats: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG</small>
                </div>

                {uploadFormData.quotation_file && (
                    <div className="alert alert-info py-2">
                        <i className="mdi mdi-file-document-outline me-2"></i>
                        {uploadFormData.quotation_file.name}
                    </div>
                )}
            </Offcanvas.Body>

            <div className="p-3 d-flex justify-content-end border-top">
                <button
                    type="button"
                    className="btn btn-secondary me-2"
                    onClick={handleClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Saving...' : 'Save'}
                </button>
            </div>
        </Offcanvas>
    );
};

export default QuotationUploadOffcanvas;

